import React, { useEffect, useMemo, useState } from "react";
import { DEFAULT_MASJID_ID } from "../config/apiConfig";
import { getMasjidServices } from "../services/moduleService";

const FALLBACK_IMG = "https://placehold.co/600x400?text=Service";

const Breadcrumb = ({ items, onNav = () => { } }) => (
  <div className="bg-gray-50 py-4">
    <div className="w-full px-4">
      <div className="flex items-center space-x-2 text-sm">
        {items.map((it, i) => (
          <div key={it.id} className="flex items-center space-x-2">
            {i > 0 && <span className="text-[#005333]">›</span>}
            <button
              onClick={() => onNav(it.id)}
              className={`transition-colors ${i === items.length - 1
                ? "text-[#005333] font-semibold"
                : "text-gray-600 hover:text-[#005333]"
                }`}
            >
              {it.label}
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function ServicesPage({
  setCurrentPage = () => { },
  // fetch-only: no "services" prop here
  masjidId = DEFAULT_MASJID_ID,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError("");
        const list = await getMasjidServices(masjidId, { signal: ac.signal });
        setItems(Array.isArray(list) ? list : []);
      } catch (e) {
        setError(e?.message || "Failed to load services.");
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [masjidId]);

  // no hardcoded fallback; only show what API returns
  const data = useMemo(() => items, [items]);

  return (
    <>
      <Breadcrumb
        items={[
          { id: "home", label: "Home" },
          { id: "services", label: "Services & Programs" },
        ]}
        onNav={setCurrentPage}
      />

      <section className="py-12 bg-gray-50">
        <div className="w-full px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">
              Services & Programs
            </h2>

          </div>

          {/* Loading */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow animate-pulse h-72" />
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center text-red-600 mb-6">{error}</div>
          )}

          {/* Empty */}
          {!loading && !error && data.length === 0 && (
            <div className="text-center text-gray-600">No services to show.</div>
          )}

          {/* Results (exact count from API) */}
          {!loading && !error && data.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.map((s, idx) => (
                <div
                  key={`${s.id ?? "svc"}-${idx}`}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-64 overflow-hidden bg-gray-100 md:h-72">
                    <a data-fancybox="services-page" href={s.image || FALLBACK_IMG} className="h-full w-full block">
                      <img
                        src={s.image || FALLBACK_IMG}
                        alt={s.title || "Service"}
                        className="h-full w-full object-contain"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG;
                        }}
                      />
                    </a>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {s.title}
                    </h3>
                    {s.description ? (
                      <p className="text-gray-600 mb-3">{s.description}</p>
                    ) : (
                      <p className="text-gray-500 mb-3 italic">Details coming soon.</p>
                    )}
                    {s.schedule && (
                      <p className="text-sm text-gray-500">⏰ {s.schedule}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* <div className="text-center mt-10">
            <button
              onClick={() => setCurrentPage("events")}
              className="bg-[#0f9e68] text-white px-8 py-3 rounded-lg hover:bg-[#005333] font-semibold"
            >
              View Upcoming Events
            </button>
          </div> */}
        </div>
      </section>
    </>
  );
}
